// oxlint-disable jsx-a11y/control-has-associated-label
import type { CodecResult } from "@schema-benchmarks/bench";
import { durationFormatter, getDuration, getTransitionName } from "@schema-benchmarks/utils";
import { ErrorBoundary } from "react-error-boundary";

import { DownloadCount } from "#src/routes/_benchmarks/-components/count";
import { Snippet } from "#src/routes/_benchmarks/_runtime/-components/table/snippet";
import { optimizeTypeProps } from "#src/routes/_benchmarks/_runtime/-constants";
import type { SortableKey } from "#src/routes/_benchmarks/_runtime/codec/-constants";
import { ToggleButton } from "#src/shared/components/button/toggle";
import { MdSymbol } from "#src/shared/components/symbol";
import { Bar } from "#src/shared/components/table/bar";
import { SortableHeaderLink } from "#src/shared/components/table/sort";
import type { SortDirection } from "#src/shared/lib/sort";

export interface CodecTableProps {
  results: Array<CodecResult>;
  encodeScaler: ReturnType<typeof Bar.getScale>;
  decodeScaler: ReturnType<typeof Bar.getScale>;
  sortBy: SortableKey;
  sortDir: SortDirection;
}

export function CodecTable({ results, encodeScaler, decodeScaler, ...sortState }: CodecTableProps) {
  return (
    <table
      className="bench-table"
      aria-label="Results"
      style={{ viewTransitionName: "result-table" }}
    >
      <thead>
        <tr>
          <SortableHeaderLink
            {...SortableHeaderLink.getProps("libraryName", sortState, { to: "/codec" })}
          >
            Library
          </SortableHeaderLink>
          <th className="action"></th>
          <th>Version</th>
          <SortableHeaderLink
            {...SortableHeaderLink.getProps("downloads", sortState, { to: "/codec" }, "descending")}
            className="numeric"
          >
            <span className="bench-table__downloads-label">
              <MdSymbol size={18}>download</MdSymbol>/wk
            </span>
          </SortableHeaderLink>
          <th>Optimizations</th>
          <SortableHeaderLink
            {...SortableHeaderLink.getProps("encode", sortState, { to: "/codec" })}
            className="numeric border-before"
            colSpan={2}
          >
            Encode
          </SortableHeaderLink>
          <th className="bar-after"></th>
          <SortableHeaderLink
            {...SortableHeaderLink.getProps("decode", sortState, { to: "/codec" })}
            className="numeric border-before"
            colSpan={2}
          >
            Decode
          </SortableHeaderLink>
          <th className="bar-after"></th>
        </tr>
      </thead>
      <tbody>
        {results.map((result) => (
          <tr
            key={result.id}
            style={{
              viewTransitionName: getTransitionName("bench-table-row", {
                libraryName: result.libraryName,
                note: result.note,
              }),
            }}
          >
            <td>
              <code className="language-text">{result.libraryName}</code>
              {result.note ? ` (${result.note})` : null}
            </td>
            <td className="action">
              {result.acceptsUnknown && (
                <ToggleButton
                  tooltip={{
                    subhead: "Accepts unknown values",
                    supporting: "This codec allows unknown input, requiring extra validation.",
                  }}
                >
                  <MdSymbol>warning</MdSymbol>
                </ToggleButton>
              )}
            </td>
            <td>
              <code className="language-text">{result.version}</code>
            </td>
            <td className="numeric">
              <ErrorBoundary fallback={null}>
                <DownloadCount libraryName={result.libraryName} />
              </ErrorBoundary>
            </td>
            <td>{optimizeTypeProps.labels[result.optimizeType].label}</td>
            <td className="action border-before">
              <Snippet code={result.encode.snippet} />
            </td>
            <td className="numeric">{durationFormatter.format(getDuration(result.encode.mean))}</td>
            <td className="bar-after">
              <Bar {...encodeScaler(result.encode.mean)} />
            </td>
            <td className="action border-before">
              <Snippet code={result.decode.snippet} />
            </td>
            <td className="numeric">{durationFormatter.format(getDuration(result.decode.mean))}</td>
            <td className="bar-after">
              <Bar {...decodeScaler(result.decode.mean)} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
